import { Link } from 'react-router-dom';
import { categories, recommendedNodes, articles, faqs } from '@/mocks/nodes';
import ArticleCard from '@/components/base/ArticleCard';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '节点指南',
  url: 'https://your-domain.com/',
  description: '免费节点获取指南，每日更新稳定可用的免费节点资源，提供使用教程与安全指南。',
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '免费节点获取指南·每日更新稳定可用',
  url: 'https://your-domain.com/',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://your-domain.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const heroBg = "https://readdy.ai/api/search-image?query=Abstract%20digital%20network%20connections%20with%20warm%20amber%20and%20coral%20gradient%20flowing%20lines%20on%20dark%20background%2C%20modern%20minimalist%20tech%20atmosphere%2C%20geometric%20node%20points%20connected%20by%20smooth%20curves%2C%20soft%20glowing%20warm%20light%20orbs%2C%20futuristic%20but%20clean%20aesthetic%2C%20no%20text%2C%20no%20people%2C%20cinematic%20lighting&width=1800&height=1000&seq=home-hero-2026&orientation=landscape";

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="免费节点获取指南 - 数字网络连接抽象背景" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight max-w-3xl">
            免费节点获取指南
            <br />
            <span className="text-primary-300">每日更新 · 稳定可用</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
            最新免费节点获取渠道、筛选方法与使用技巧，涵盖稳定性测试、速度优化及安全注意事项，帮助你快速找到可用的免费节点资源
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/category/subscription"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-link-m"></i>
              获取订阅链接
            </Link>
            <Link
              to="/category/tutorials"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 text-white backdrop-blur-sm rounded-full font-medium text-sm hover:bg-white/25 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              查看使用教程
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="w-full bg-background-50 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '200+', label: '每日更新节点' },
            { value: '6', label: '节点分类' },
            { value: '99.5%', label: '平均在线率' },
            { value: '15+', label: '专业教程文章' },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center">
              <span className="font-heading text-2xl md:text-3xl font-bold text-accent-500">{stat.value}</span>
              <span className="text-sm text-foreground-500 mt-1">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Recommended Nodes */}
      <section className="w-full py-14 md:py-18 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              热门推荐节点
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              以下节点经实测验证，稳定性与速度表现优异，推荐优先选用
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedNodes.map((node) => (
              <div
                key={node.id}
                className="bg-background-50 rounded-xl border border-background-200/70 p-5 hover:border-primary-200 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    node.type === 'SSR'
                      ? 'bg-primary-100 text-primary-700'
                      : node.type === 'V2Ray'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-800'
                  }`}>
                    {node.type}
                  </span>
                  <span className="text-xs text-foreground-500">{node.region}</span>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-3">{node.name}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-speed-up-line text-accent-500"></i>
                    <span className="text-foreground-600">速度 {node.speed}%</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-check-double-line text-accent-500"></i>
                    <span className="text-foreground-600">在线率 {node.uptime}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-timer-line text-accent-500"></i>
                    <span className="text-foreground-600">延迟 {node.delay}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-accent-500"></i>
                    <span className="text-foreground-600">{node.encryption}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="w-full py-14 md:py-18 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              浏览节点分类
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              按协议类型和使用场景分类，快速找到你需要的内容
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-100 rounded-xl border border-background-200/70 p-5 hover:border-accent-200 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 shrink-0 group-hover:bg-accent-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1 group-hover:text-accent-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{cat.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="w-full py-14 md:py-18 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                最新文章
              </h2>
              <p className="text-foreground-500">每日更新优质节点教程与资讯</p>
            </div>
            <Link
              to="/category/tutorials"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.slice(0, 6).map((article) => (
              <ArticleCard key={article.slug} {...article} />
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/tutorials"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600"
            >
              查看全部文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-14 md:py-18 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-500">关于免费节点的常见疑问，这里都有答案</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <h3 className="font-heading text-base font-bold text-foreground-900 pr-4">{faq.question}</h3>
                  <div className="w-6 h-6 flex items-center justify-center shrink-0 rounded-full bg-background-200 group-open:bg-primary-100 transition-colors">
                    <i className="ri-add-line text-sm text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                  </div>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-14 md:py-18 bg-accent-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
            准备好开始了吗？
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            浏览我们的节点资源库，找到最适合你的免费节点。每日更新，稳定可靠。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/subscription"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-accent-700 rounded-full font-medium text-sm hover:bg-white/90 transition-colors whitespace-nowrap"
            >
              <i className="ri-link-m"></i>
              获取订阅链接
            </Link>
            <Link
              to="/category/tutorials"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 text-white backdrop-blur-sm rounded-full font-medium text-sm hover:bg-white/25 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              新手上路教程
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}